/**
 * Which deployment this bundle belongs to — the web client's answer to `isPackaged`.
 *
 * Nothing here is decided at build time. `vite build` inlines the *placeholder* that
 * `.env.production` holds (`production`, not a value), and `web/env.sh`
 * rewrites it in the built files when the container starts. One image therefore serves both
 * test.web.dictaris.ro and web.dictaris.ro, and promoting a tested build to production is a
 * re-tag rather than a rebuild — the same arrangement `platform` uses.
 *
 * The environment *name* is injected rather than the API URL, so the host table in
 * `@dictaris/core` stays the one place a Dictaris URL is written down. A second client
 * carrying its own copy of `https://api.dictaris.ro` is one deployment away from a test build
 * talking to production, which is the failure that table exists to prevent.
 */

import { type DictarisEnvironment, parseEnvironment } from '@dictaris/core';

/**
 * Escape hatch for a deployment that is none of the three — a reviewer's branch on a
 * throwaway host, a customer trial against an API that is not in the table yet. Left unset in
 * every normal deployment, and `web/env.sh` does not require it.
 */
const apiBaseUrlOverride = import.meta.env.VITE_API_BASE_URL_OVERRIDE;

let resolved: DictarisEnvironment | null = null;

/** Where the built page carries the environment `web/env.sh` substituted into it. */
const ENVIRONMENT_META = 'dictaris-environment';

/**
 * The environment name, read out of the document at run time.
 *
 * It has to come from the DOM, and the reason is specific rather than stylistic. It used to be
 * `import.meta.env.VITE_DICTARIS_ENVIRONMENT`, which `.env.production` sets to the literal
 * `production` for `env.sh` to rewrite in the built files. That made the
 * argument to `parseEnvironment` a compile-time constant, so Rollup evaluated the guard —
 * `value === 'production' || value === 'test' || value === 'local'` — found all three false
 * against the placeholder, and deleted the branch as dead code. What shipped was a function
 * whose whole body was the `throw`:
 *
 *     function parseEnvironment(value) { throw new Error(`Unknown Dictaris environment ...`) }
 *
 * `env.sh` then rewrote the string correctly, and the app still refused to start with
 * "Unknown Dictaris environment \"test\"" — an error naming a value that is in its own list of
 * accepted ones. Substituting text into a bundle cannot restore a branch the bundler has
 * already proved unreachable.
 *
 * A meta tag is opaque to the bundler, so the guard survives. It is also in `index.html`,
 * which Caddy serves `no-cache` — unlike `/assets/*`, which is `immutable` for a year under
 * filenames hashed *before* `env.sh` edits them, and would therefore pin one deployment's
 * environment into a browser indefinitely.
 *
 * Not a `<script>`: the edge CSP is `script-src 'self'`, which an inline one would need an
 * exception for.
 */
function environmentFromDocument(): string | null {
  return document.querySelector(`meta[name="${ENVIRONMENT_META}"]`)?.getAttribute('content') ?? null;
}

/**
 * Parsed once and cached, because it is read on every API call and the failure it can raise is
 * a start-up failure rather than a per-request one.
 */
export function webEnvironment(): DictarisEnvironment {
  if (resolved === null) {
    // The dev server runs no env.sh, so the meta still holds the placeholder and the answer
    // comes from .env.local as before. `import.meta.env.DEV` is a build-time constant, so the
    // branch not taken is removed — which is what keeps the production path's argument
    // something the bundler cannot fold.
    resolved = parseEnvironment(
      import.meta.env.DEV ? import.meta.env.VITE_DICTARIS_ENVIRONMENT : environmentFromDocument(),
    );
  }

  return resolved;
}

/** The override, if this deployment set one. Empty and unset both mean "use the table". */
export function webApiBaseUrlOverride(): string | null {
  return apiBaseUrlOverride && apiBaseUrlOverride.length > 0 ? apiBaseUrlOverride : null;
}

/**
 * Reads the configuration eagerly so a misconfigured container fails at boot with one clear
 * message, instead of on whichever screen happens to make the first API call.
 */
export function assertWebEnvironmentConfigured(): void {
  webEnvironment();
}
